/**
 * Admin V3 — Corporate activate/suspend toggle (AV3 A-6)
 *
 * Per-row lifecycle control. Suspends an active account or reactivates a
 * suspended one via PATCH /enterprises/{id} with { status } (RBAC:
 * enterprise:update — the canonical update endpoint whose allowed_fields include
 * "status"). Reversible, no confirm needed. No backend change; each row owns its
 * own mutation hook.
 */
import React from 'react';
import { Loader2, Ban, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const CorporateStatusToggle = ({ enterprise, onDone, locale = DEFAULT_LOCALE }) => {
  const m = useAdminMutation(`/enterprises/${enterprise?.id || '_'}`, 'patch');
  const { showSuccess, showError } = useToast();

  const isActive = (enterprise?.status || 'active') === 'active';
  const nextStatus = isActive ? 'suspended' : 'active';

  const toggle = () => {
    m.mutate(
      { status: nextStatus },
      {
        onSuccess: () => {
          showSuccess(t(isActive ? 'corporate.status.suspended' : 'corporate.status.reactivated', locale));
          if (onDone) onDone();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('corporate.status.error', locale)),
      },
    );
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`h-7 px-2 text-[12px] ${isActive ? 'text-amber-700 hover:bg-amber-50' : 'text-emerald-700 hover:bg-emerald-50'}`}
      onClick={toggle}
      disabled={m.loading}
      data-testid="adminv3-corporate-status-toggle"
    >
      {m.loading
        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        : isActive ? <Ban className="h-3.5 w-3.5 mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
      {t(isActive ? 'corporate.action.suspend' : 'corporate.action.reactivate', locale)}
    </Button>
  );
};

export default CorporateStatusToggle;
